import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Circle from '@mui/icons-material/FiberManualRecord'

import type { RegionSortableField } from 'common/api/v1/types'
import { AppDispatch, useRoutes } from '../../store'
import { isEditableRegion, usePageParamsFilteredSelector, useRegionsSelector, useUser } from '../../utils'
import { Link } from '../common/Link'
import Table, { type TableConfig } from '../common/Table'
import { MissingContent } from '../common/MissingContent'
import Wrapper from '../common/Wrapper'
import {
  ListRegionFilterKey,
  makeListRegionsFilter,
  mapRegionFilterToUrlParam,
  mapUrlParamToRegionFilter,
} from './listRegionFilter'
import type { EnrichedRegion } from '../../api/nm-types'
import { UrlParamFilteredSearchBar } from '../common/Filters/FilterView/FilteredSearchBar'
import { ActionMenu } from '../common/Table/ActionMenu'
import { useDispatch } from 'react-redux'
import AdjustIcon from '@mui/icons-material/Adjust'
import SettingsIcon from '@mui/icons-material/Settings'
import { setDefaultRegion } from '../../redux/actions/regionsActions'

const styles = {
  defaultIndicatorWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  defaultIndicator: {
    marginLeft: '10px',
  },
}

export const List = () => {
  const { regions, total, loading } = usePageParamsFilteredSelector(useRegionsSelector)
  const user = useUser()
  const routes = useRoutes()
  const dispatch = useDispatch<AppDispatch>()

  const tableConfig: TableConfig<EnrichedRegion, RegionSortableField> = [
    {
      title: 'name',
      getValue: (region) => (
        <Link available={isEditableRegion(user)} to={routes.regionsUpdate({ id: region.id })} underline="hover">
          {region.name}
        </Link>
      ),
      sorting: { byParameter: 'name' },
    },
    {
      title: 'type',
      getValue: ({ _externalText }) => _externalText,
      sorting: { byParameter: 'external' },
    },
    {
      title: 'default',
      getValue: ({ default_region }) => {
        if (!default_region) {
          return
        }

        return (
          <Box sx={styles.defaultIndicatorWrapper}>
            <Circle sx={styles.defaultIndicator} />
          </Box>
        )
      },
      sorting: { byParameter: 'default_region' },
      props: { noTypography: true },
    },
    // <ActionMenu region={row} disabled={!isEditableRegion(user)} />,
    {
      title: '',
      getValue: (region) =>
        isEditableRegion(user) && (
          <ActionMenu
            options={[
              {
                label: 'Edit',
                icon: <SettingsIcon />,
                href: routes.regionsUpdate({ id: region.id }),
                'data-test-id': 'edit-region',
              },
              {
                label: 'Set as default',
                icon: <AdjustIcon />,
                onClick: () => {
                  dispatch(setDefaultRegion({ regionId: region.id, regionName: region.name }))
                },
              },
            ]}
          />
        ),

      props: { noTypography: true },
    },
  ]

  const filters = makeListRegionsFilter()
  return (
    <Wrapper name="Regions">
      <Toolbar disableGutters>
        <div style={{ flexShrink: 1, flexGrow: 1 }}>
          <UrlParamFilteredSearchBar
            urlParamCacheKey={'regions'}
            mapUrlParamToFilterFn={mapUrlParamToRegionFilter}
            mapFilterToUrlParamFn={mapRegionFilterToUrlParam}
            searchTokenFilters={filters}
            rawTextFilter={filters.find((f) => f.key === ListRegionFilterKey.name)!}
          />
        </div>
      </Toolbar>
      <Table<EnrichedRegion>
        emptyMessageComponent={<MissingContent message="No regions available" />}
        config={tableConfig}
        data={regions}
        pending={loading}
        pagination={{ total, useUrlSearchParams: true }}
      />
    </Wrapper>
  )
}
